/**
 * ArchivingOnCompletionSection Component
 *
 * Whether and how many days after completion a document is archived — a section of its own,
 * apart from the conditions.
 */
import { useEffect, useId, useState } from 'react';
import { MAX_ARCHIVE_AFTER_DAYS } from '@insio/shared-types';
import { Input, InputWrapper, Paper, Toggle } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** The typed days as a whole number from 0 to the maximum, otherwise `null`. */
const parseDays = (value: string): number | null => {
  if (!/^\d+$/.test(value.trim())) return null;
  const days = Number(value);
  return days <= MAX_ARCHIVE_AFTER_DAYS ? days : null;
};

export interface ArchivingOnCompletionSectionProps {
  /** Translation namespace under `systemSettings.automation`. */
  namespace: 'invoiceClosure' | 'purchaseOrderClosure' | 'requisitionClosure';
  checked: boolean;
  onChange: (isArchivingOnCompletion: boolean) => void;
  archiveAfterDays: number;
  onArchiveAfterDaysChange: (archiveAfterDays: number) => void;
  /** Locks the switch and the days, e.g. for whoever may only read the settings. */
  disabled?: boolean;
  /** Prefix of the section's and the controls' test ids. */
  testIdPrefix: string;
}

export function ArchivingOnCompletionSection({
  namespace,
  checked,
  onChange,
  archiveAfterDays,
  onArchiveAfterDaysChange,
  disabled = false,
  testIdPrefix
}: ArchivingOnCompletionSectionProps) {
  const t = useFeatureTranslation(`systemSettings.automation.${namespace}`);
  const daysInputId = useId();

  const [daysDraft, setDaysDraft] = useState(String(archiveAfterDays));
  const [isDaysInvalid, setIsDaysInvalid] = useState(false);

  // Back to the stored days when they change or the switch flips; an unsaved typo does not stay.
  useEffect(() => {
    setDaysDraft(String(archiveAfterDays));
    setIsDaysInvalid(false);
  }, [archiveAfterDays, checked]);

  /** Stored on blur or Enter when changed; an invalid value is reported and kept unsaved. */
  const handleDaysCommit = () => {
    if (disabled) return;
    const days = parseDays(daysDraft);
    if (days === null) {
      setIsDaysInvalid(true);
      return;
    }
    if (days !== archiveAfterDays) onArchiveAfterDaysChange(days);
  };

  return (
    <Paper heading='stripe' bleed padding='none' testId={`${testIdPrefix}-archiving-section`}>
      <Paper.Title>
        <h2>{t('archiving.title')}</h2>
      </Paper.Title>
      <Paper.Action>
        <Toggle
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          ariaLabel={t('archiving.title')}
          testId={`${testIdPrefix}-archiving`}
        />
      </Paper.Action>
      <Paper.Content className='space-y-md'>
        <p className='text-sm text-foreground-muted'>{t('archiving.description')}</p>
        {checked && (
          <InputWrapper
            htmlFor={daysInputId}
            label={t('archiving.days.label')}
            hint={t('archiving.days.hint')}
            isError={isDaysInvalid}
            errorText={t('archiving.days.invalid', { max: MAX_ARCHIVE_AFTER_DAYS })}
            className='max-w-xs'
          >
            <Input
              id={daysInputId}
              type='number'
              inputMode='numeric'
              min={0}
              max={MAX_ARCHIVE_AFTER_DAYS}
              step={1}
              value={daysDraft}
              disabled={disabled}
              rightContent={t('archiving.days.unit')}
              onChange={e => {
                setDaysDraft(e.target.value);
                setIsDaysInvalid(false);
              }}
              onBlur={handleDaysCommit}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              testId={`${testIdPrefix}-archive-after-days`}
            />
          </InputWrapper>
        )}
      </Paper.Content>
    </Paper>
  );
}
